import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { criarUsuario, definirFuncoes, mensagemDoErro } from '@/services/usuariosService';
import { PAPEIS, PAPEIS_DA_CLINICA } from '@/lib/constants';
import { FUNCOES, funcoesDe, podeTerFuncao } from '@/equipe/funcoes';

const ROTULO_FUNCAO = { sdr: 'SDR', negociador: 'Negociador' };
const AVISO_FUNCAO_RECEPCAO = 'Recepção não recebe função de vendas — troque o acesso para Vendas primeiro.';

/**
 * Cadastro de um membro da equipe.
 *
 * Cria de uma vez a conta no Auth, as claims de tenancy (clinicaId + papel), o
 * documento na clínica e o do mapa global — tudo pela callable, porque nada
 * disso o cliente consegue (nem deve conseguir) fazer sozinho.
 */

// A senha mínima é a do servidor (guardas.js:52). Repetida aqui só para o
// navegador avisar antes de gastar uma ida; quem decide continua sendo lá.
const MINIMO_SENHA = 8;

// ★ VP — `funcoes: null` = "sugerida pelo acesso" (default derivado); vira lista quando o gestor mexe.
const VAZIO = { nome: '', email: '', senha: '', papel: 'recepcao', funcoes: null };

export default function DialogoNovoMembro({ aberto, aoFechar, clinicaId }) {
  const [dados, setDados] = useState(VAZIO);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setDados(VAZIO);
    setErro(null);
  }, [aberto]);

  const mudar = (campo) => (e) => setDados((d) => ({ ...d, [campo]: e.target.value }));
  // Trocar o acesso volta a função para a sugerida — a marcação anterior era de outro acesso.
  const mudarPapel = (e) => setDados((d) => ({ ...d, papel: e.target.value, funcoes: null }));
  const funcoesAtuais = dados.funcoes ?? funcoesDe({ papel: dados.papel });
  const alternarFuncao = (f) => setDados((d) => {
    const atuais = d.funcoes ?? funcoesDe({ papel: d.papel });
    return { ...d, funcoes: atuais.includes(f) ? atuais.filter((x) => x !== f) : [...atuais, f] };
  });

  function fechar() {
    setErro(null);
    aoFechar();
  }

  async function salvar(evento) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const { data } = await criarUsuario({
        clinicaId,
        nome: dados.nome.trim(),
        email: dados.email.trim().toLowerCase(),
        senha: dados.senha,
        papel: dados.papel,
      });
      // ★ VP — só grava `funcoes` se o gestor mexeu na sugestão: sem mexer, o
      // default derivado do acesso já dá exatamente a lista sugerida.
      if (dados.funcoes && data?.uid) await definirFuncoes(clinicaId, data.uid, dados.funcoes);
      // A lista se atualiza sozinha: ouvirUsuariosDaClinica é um listener.
      fechar();
    } catch (e) {
      console.error('[OdontoSDR] Falha ao criar membro:', e);
      setErro(mensagemDoErro(e, 'Não foi possível cadastrar o membro.'));
    } finally {
      setSalvando(false);
    }
  }

  const papelEscolhido = PAPEIS[dados.papel];

  return (
    <Dialogo titulo="Adicionar membro" aberto={aberto} aoFechar={fechar} largura={480}>
      <form className="formulario" onSubmit={salvar}>
        <div className="campo">
          <label htmlFor="nm-nome">Nome</label>
          <input id="nm-nome" required autoFocus value={dados.nome} onChange={mudar('nome')} />
        </div>

        <div className="campo">
          <label htmlFor="nm-email">E-mail</label>
          <input
            id="nm-email"
            type="email"
            required
            value={dados.email}
            onChange={mudar('email')}
          />
          <p className="campo-ajuda">É com ele que a pessoa entra na plataforma.</p>
        </div>

        <div className="campo">
          <label htmlFor="nm-senha">Senha provisória</label>
          <input
            id="nm-senha"
            type="text"
            required
            minLength={MINIMO_SENHA}
            placeholder={`mínimo ${MINIMO_SENHA} caracteres`}
            value={dados.senha}
            onChange={mudar('senha')}
          />
          {/* Em texto claro de propósito: quem cadastra precisa LER a senha para
              passar à pessoa. Esconder aqui não protege ninguém — só obrigaria a
              anotar em outro lugar, que é pior. */}
          <p className="campo-ajuda">
            Combine com a pessoa que ela troque no primeiro acesso.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="nm-papel">Acesso</label>
          <select id="nm-papel" required value={dados.papel} onChange={mudarPapel}>
            {PAPEIS_DA_CLINICA.map((id) => (
              <option key={id} value={id}>
                {PAPEIS[id].label}
              </option>
            ))}
          </select>
          {papelEscolhido && <p className="campo-ajuda">{papelEscolhido.descricao}</p>}
        </div>

        <div className="campo">
          <span className="rotulo">Funções</span>
          <div className="funcoes-membro" aria-label="Funções do novo membro">
            {FUNCOES.map((f) => (
              <label key={f} className="check-inline">
                <input
                  type="checkbox"
                  checked={funcoesAtuais.includes(f)}
                  disabled={!podeTerFuncao({ papel: dados.papel })}
                  onChange={() => alternarFuncao(f)}
                />
                {ROTULO_FUNCAO[f]}
              </label>
            ))}
          </div>
          <p className="campo-ajuda">
            {podeTerFuncao({ papel: dados.papel })
              ? 'Sugerida pelo acesso; ajuste se esta pessoa também negocia (ou só negocia).'
              : AVISO_FUNCAO_RECEPCAO}
          </p>
        </div>

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={fechar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={salvando}>
            {salvando ? 'Cadastrando…' : 'Cadastrar membro'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
